import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Paper,
	Radio,
	RadioGroup,
	Snackbar,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { fetchUserSummaryCached } from 'features/salesOrder/userSummaryCache';
import { IUserSummary } from 'features/salesOrder/types';
import { getMcpUsers, saveMcpUser } from '../../services';
import { IMcpAreaOption, IMcpUser } from '../../types';
import {
	COMMERCIAL_LABELS,
	CommercialChoice,
	McpUserForm,
	NOTE_MAX,
	describeCommercial,
	describeMcpError,
	emptyForm,
	formToBody,
	formatDateTime,
	sameAsSaved,
	userToForm,
	validateForm,
} from '../../helpers';

/** Los usuarios del Power: cargando, la lista, o no se pudieron cargar. */
type PowerUsersState = IUserSummary[] | 'loading' | 'failed';

interface DialogProps {
	/** `null` = alta de un usuario nuevo. */
	user: IMcpUser | null;
	areas: IMcpAreaOption[];
	powerUsers: PowerUsersState;
	existing: Set<string>;
	onClose: () => void;
	onSaved: (user: IMcpUser) => void;
}

const powerUserLabel = (user: IUserSummary): string => `${user.name} (${user.username})`;

/** Alta o cambio de un usuario del conector. Se monta al abrirse: arranca con lo de ese usuario. */
function McpUserDialog({ user, areas, powerUsers, existing, onClose, onSaved }: DialogProps): JSX.Element {
	const isNew = user === null;
	const [form, setForm] = useState<McpUserForm>(() => (user ? userToForm(user) : emptyForm()));
	const [saving, setSaving] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);
	const formError = validateForm(form);
	const duplicate = isNew && existing.has(form.username.trim().toLowerCase());

	const candidates = useMemo(
		() =>
			(Array.isArray(powerUsers) ? powerUsers : [])
				.filter((candidate) => candidate.active && !existing.has(candidate.username.toLowerCase()))
				.sort((a, b) => a.name.localeCompare(b.name)),
		[powerUsers, existing],
	);

	const toggleArea = (key: string, checked: boolean) =>
		setForm((current) => ({
			...current,
			areas: checked ? [...current.areas, key] : current.areas.filter((area) => area !== key),
		}));

	const handleSave = async () => {
		if (formError || duplicate) return;
		const body = formToBody(
			form,
			areas.map((area) => area.key),
		);
		if (user && sameAsSaved(body, user)) {
			onClose();
			return;
		}
		setSaving(true);
		setApiError(null);
		try {
			// Al agregar no pisa a nadie que ya exista; al editar no pisa un cambio que hizo otro mientras tanto.
			const { data } = await saveMcpUser(
				user ? { ...body, expectedUpdatedAt: user.updatedAt } : { ...body, mode: 'create' },
			);
			// El PUT no devuelve el uso: se conserva el que ya mostraba la tabla.
			onSaved({
				...data.data,
				callsLast30Days: user?.callsLast30Days ?? 0,
				lastCallAt: user?.lastCallAt ?? null,
			});
		} catch (error) {
			setApiError(describeMcpError(error, 'save'));
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isNew ? 'Agregar al conector' : `Acceso de ${user.username}`}</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ mt: 1 }}>
					{isNew && powerUsers === 'loading' && (
						<TextField
							size="small"
							label="Usuario del Power"
							value=""
							disabled
							helperText="Cargando los usuarios del Power…"
						/>
					)}
					{isNew && Array.isArray(powerUsers) && (
						<Autocomplete
							options={candidates}
							getOptionLabel={powerUserLabel}
							value={candidates.find((candidate) => candidate.username === form.username) ?? null}
							onChange={(_, value) => setForm((current) => ({ ...current, username: value?.username ?? '' }))}
							disabled={saving}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label="Usuario del Power"
									helperText="Entra al conector con su usuario y contraseña del Power"
								/>
							)}
						/>
					)}
					{isNew && powerUsers === 'failed' && (
						<TextField
							size="small"
							label="Usuario del Power"
							value={form.username}
							onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
							disabled={saving}
							helperText="No se pudo cargar la lista del Power: escribí el usuario con el que entra al Power"
						/>
					)}
					{duplicate && <Alert severity="warning">Ese usuario ya está en la lista: editalo desde la tabla.</Alert>}

					<FormControl component="fieldset" disabled={saving}>
						<FormLabel component="legend">Áreas</FormLabel>
						<FormGroup>
							{areas.map((area) => (
								<FormControlLabel
									key={area.key}
									control={
										<Checkbox
											checked={form.areas.includes(area.key)}
											onChange={(event) => toggleArea(area.key, event.target.checked)}
										/>
									}
									label={area.label}
								/>
							))}
						</FormGroup>
						<FormHelperText>
							Solo ve las herramientas de sus áreas. El CRM y las vistas por vendedor además piden rol Admin en el
							Power.
						</FormHelperText>
					</FormControl>
					{form.enabled && form.areas.length === 0 && (
						<Alert severity="info">Sin áreas solo ve quién es y la lista de agencias.</Alert>
					)}

					<FormControl component="fieldset" disabled={saving}>
						<FormLabel component="legend">Costos, márgenes y proveedores</FormLabel>
						<RadioGroup
							value={form.commercial}
							onChange={(event) =>
								setForm((current) => ({ ...current, commercial: event.target.value as CommercialChoice }))
							}
						>
							{(['role', 'yes', 'no'] as CommercialChoice[]).map((choice) => (
								<FormControlLabel key={choice} value={choice} control={<Radio />} label={COMMERCIAL_LABELS[choice]} />
							))}
						</RadioGroup>
						<FormHelperText>
							Según su rol: los ven Admin y Super Admin, y el resto no (el rol con el que se conectó; si se lo cambian,
							toma el nuevo al volver a conectarse). Sí o No: lo decidís acá, sin importar el rol, y vale desde la
							próxima consulta.
						</FormHelperText>
					</FormControl>

					<FormControlLabel
						control={
							<Switch
								checked={form.enabled}
								onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
								disabled={saving}
							/>
						}
						label="Puede entrar al conector"
					/>
					{!isNew && user.enabled && !form.enabled && (
						<Alert severity="warning">
							Se le cierran las sesiones abiertas. Para volver a entrar lo tenés que habilitar de nuevo y se tiene que
							conectar otra vez.
						</Alert>
					)}

					<TextField
						size="small"
						label="Nota"
						value={form.note}
						onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
						disabled={saving}
						multiline
						minRows={2}
						error={form.note.trim().length > NOTE_MAX}
						helperText={`Opcional, hasta ${NOTE_MAX} caracteres (por ejemplo, para qué lo usa)`}
					/>
					{apiError && <Alert severity="error">{apiError}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={saving || formError !== null || duplicate}
					startIcon={saving ? <CircularProgress size={16} /> : undefined}
				>
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/**
 * Quién entra al conector de datos para Claude, qué áreas ve y si ve costos. Solo para Super Admin (la página no se
 * muestra a otros roles y la API rechaza a cualquier otro).
 */
export function McpUsersManagement(): JSX.Element {
	const [users, setUsers] = useState<IMcpUser[]>([]);
	const [areas, setAreas] = useState<IMcpAreaOption[]>([]);
	const [total, setTotal] = useState(0);
	const [powerUsers, setPowerUsers] = useState<PowerUsersState>('loading');
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [dialog, setDialog] = useState<{ user: IMcpUser | null } | null>(null);
	const [saved, setSaved] = useState<string | null>(null);

	useEffect(() => {
		let alive = true;
		getMcpUsers()
			.then(({ data }) => {
				if (!alive) return;
				setUsers(data.data);
				setAreas(data.areas);
				setTotal(data.pagination.total);
			})
			.catch((error) => {
				if (alive) setLoadError(describeMcpError(error, 'load'));
			})
			.finally(() => {
				if (alive) setLoading(false);
			});
		// Los nombres y el buscador salen de los usuarios del Power. Si no cargan, la lista se muestra igual.
		fetchUserSummaryCached()
			.then((data) => {
				if (alive) setPowerUsers(data);
			})
			.catch(() => {
				if (alive) setPowerUsers('failed');
			});
		return () => {
			alive = false;
		};
	}, []);

	const byUsername = useMemo(
		() => new Map((Array.isArray(powerUsers) ? powerUsers : []).map((user) => [user.username.toLowerCase(), user])),
		[powerUsers],
	);
	const existing = useMemo(() => new Set(users.map((user) => user.username.toLowerCase())), [users]);
	const areaLabel = useMemo(() => new Map(areas.map((area) => [area.key, area.label])), [areas]);

	const visible = useMemo(() => {
		const text = search.trim().toLowerCase();
		return users
			.filter((user) => {
				if (text === '') return true;
				const name = byUsername.get(user.username.toLowerCase())?.name ?? '';
				return [user.username, name].some((value) => value.toLowerCase().includes(text));
			})
			.sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.username.localeCompare(b.username));
	}, [users, search, byUsername]);

	const handleSaved = (user: IMcpUser) => {
		setUsers((current) =>
			current.some((row) => row.id === user.id)
				? current.map((row) => (row.id === user.id ? user : row))
				: [...current, user],
		);
		if (!existing.has(user.username.toLowerCase())) setTotal((current) => current + 1);
		setDialog(null);
		setSaved(`Se guardó el acceso de ${user.username}`);
	};

	if (loading) return <CircularProgress aria-label="Cargando la lista del conector" />;
	if (loadError) return <Alert severity="error">{loadError}</Alert>;

	return (
		<Stack spacing={2}>
			<Paper sx={{ p: 2 }}>
				<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
					<TextField
						size="small"
						label="Buscar"
						placeholder="Nombre o usuario"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
					<Typography variant="body2" color="text.secondary">
						{visible.length} {visible.length === 1 ? 'usuario' : 'usuarios'}
					</Typography>
					<Box flexGrow={1} />
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ user: null })}>
						Agregar usuario
					</Button>
				</Box>
			</Paper>
			{total > users.length && (
				<Alert severity="warning">
					Se muestran {users.length} de {total} usuarios.
				</Alert>
			)}

			<TableContainer component={Paper}>
				<Table size="small" aria-label="Usuarios del conector">
					<TableHead>
						<TableRow>
							<TableCell>Usuario</TableCell>
							<TableCell>Áreas</TableCell>
							<TableCell>Costos y proveedores</TableCell>
							<TableCell>Estado</TableCell>
							<TableCell>Último ingreso</TableCell>
							<TableCell align="right">Consultas (30 días)</TableCell>
							<TableCell>Último cambio</TableCell>
							<TableCell align="center">Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visible.length === 0 ? (
							<TableRow>
								<TableCell colSpan={8} align="center">
									{users.length === 0 ? 'Todavía no hay nadie en el conector.' : 'No hay usuarios con esa búsqueda.'}
								</TableCell>
							</TableRow>
						) : (
							visible.map((user) => {
								const power = byUsername.get(user.username.toLowerCase());
								return (
									<TableRow key={user.id} hover>
										<TableCell>
											<Typography variant="body2">{power?.name ?? user.username}</Typography>
											{/* Sin la lista del Power no se sabe: no se dice nada. */}
											{(power || Array.isArray(powerUsers)) && (
												<Typography variant="caption" color="text.secondary">
													{power
														? `${user.username}${power.active ? '' : ' · inactivo en el Power'}`
														: 'no aparece en el Power'}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Box display="flex" gap={0.5} flexWrap="wrap">
												{user.areas.length === 0
													? '—'
													: user.areas.map((area) => (
															<Chip key={area} size="small" label={areaLabel.get(area) ?? area} />
													  ))}
											</Box>
										</TableCell>
										<TableCell>{describeCommercial(user.commercialAccess)}</TableCell>
										<TableCell>
											<Chip
												size="small"
												color={user.enabled ? 'success' : 'default'}
												label={user.enabled ? 'Habilitado' : 'Deshabilitado'}
											/>
										</TableCell>
										<TableCell>{formatDateTime(user.lastLoginAt)}</TableCell>
										<TableCell align="right">{user.callsLast30Days}</TableCell>
										<TableCell>
											<Typography variant="body2">{formatDateTime(user.updatedAt)}</Typography>
											{user.updatedBy && (
												<Typography variant="caption" color="text.secondary">
													por {user.updatedBy}
												</Typography>
											)}
										</TableCell>
										<TableCell align="center">
											<Button
												size="small"
												startIcon={<EditIcon />}
												onClick={() => setDialog({ user })}
												aria-label={`Editar el acceso de ${user.username}`}
											>
												Editar
											</Button>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{dialog && (
				<McpUserDialog
					user={dialog.user}
					areas={areas}
					powerUsers={powerUsers}
					existing={existing}
					onClose={() => setDialog(null)}
					onSaved={handleSaved}
				/>
			)}

			<Snackbar
				open={saved !== null}
				autoHideDuration={6000}
				onClose={() => setSaved(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={() => setSaved(null)} severity="success">
					{saved}
				</Alert>
			</Snackbar>
		</Stack>
	);
}
